import { EditorState } from 'lexical';
import { ListStore } from '@list-store';
import { action, computed, makeObservable, observable } from 'mobx';
import { AdditionalInfoType, AdditionalInfoView, Priority } from '../../types';
import { compareInfos, sortDirectives } from '../../helpers/sort-helpers';
import { ActiveRowAction, SortDirection, UnreadInfos, UnreadInfo } from './types';
import { NarrativePagination } from '@mark43/cad-api';

export interface AdditionalInfoStoreConfig {
    sortDirection?: SortDirection;
    filter?: AdditionalInfoType[];
    firstUnreadInfo?: UnreadInfo;
    onAllRead?: () => void;
    /** Called when rows are removed from the list (user delete or websocket
     *  eviction) so external unread state (event notifications) can drop
     *  entries whose rows can never be scrolled into view again. */
    onInfosRemoved?: (ids: number[]) => void;
}

const { USER, SYSTEM } = AdditionalInfoType;

interface Data {
    filter?: AdditionalInfoType[];
    sortDirection?: SortDirection;
    unreads: UnreadInfo[];
    agencyEventNumberToIdMap: Record<string, number>;
    isPageReloading: boolean;
    pageFetchFailed: boolean;
    pagination: NarrativePagination;
    isFetchingNextPage: boolean;
    initialSocketConnectionMade: boolean;
    editorState?: EditorState;
    isHighPriority: boolean;
    pinnedEntries: AdditionalInfoView[];
    isPinnedSectionExpanded: boolean;
    // Single home for the in-flight row action (edit or delete-confirm) — at
    // most one at a time. Scoped by (id + row kind) so a pinned entry and its
    // chronological twin, which share info.id, are disambiguated and only the
    // acting row is live.
    activeRowAction: ActiveRowAction | null;
    hiddenAgencyTypeIds: Set<number>;
    participatingAgencyTypeIds: Set<number>;
    agencyTypeFilterEntityId?: number;
    // Live cursor — diverges from pagination.newestEntryId over time. Seeded from
    // pagination.newestEntryId on bootstrap/aroundId, advanced on each new entry
    // observed (WS create / CATCHUP scroll), walked back when the row at the tip
    // is deleted. Drives "Jump to Latest" target + button-enabled state.
    latestKnownId?: number;
}

const isUnreadPriority = (
    priority: AdditionalInfoView['priority']
): priority is UnreadInfo['priority'] =>
    priority === Priority.STANDARD || priority === Priority.HIGH;

const removeInvalidInitialUnreads = (info: any): boolean => {
    if (!info || typeof info !== 'object') return false;
    if (!Number.isInteger(info.id) || info.id <= 0) return false;
    if (info.priority !== 'STANDARD_UPDATE' && info.priority !== 'HIGH_PRIORITY_UPDATE')
        return false;
    return true;
};

const setsEqual = (a: Set<number>, b: Set<number>): boolean =>
    a.size === b.size && [...a].every((id) => b.has(id));

export class AdditionalInfoStore {
    public readonly listStore: ListStore<AdditionalInfoView> = new ListStore<AdditionalInfoView>();

    public data: Data = {
        filter: undefined,
        sortDirection: undefined,
        unreads: [],
        agencyEventNumberToIdMap: {},
        isPageReloading: false,
        pageFetchFailed: false,
        // hasOlderPage:true — bootstrap fetch hasn't happened yet, but the
        // renderer's bottom-edge fetch should fire on mount (DESC sort: bottom
        // = older). Cleared by the bootstrap response, which sets the real
        // value from the BE.
        pagination: { hasOlderPage: true },
        isFetchingNextPage: false,
        initialSocketConnectionMade: false,
        editorState: undefined,
        isHighPriority: false,
        pinnedEntries: [],
        isPinnedSectionExpanded: false,
        activeRowAction: null,
        hiddenAgencyTypeIds: new Set<number>(),
        participatingAgencyTypeIds: new Set<number>(),
        agencyTypeFilterEntityId: undefined,
        latestKnownId: undefined,
    };

    onAllRead?: () => void;

    onSyncUnreadPriority?: (id: number, priority: UnreadInfo['priority']) => void;
    onInfosRemoved?: (ids: number[]) => void;

    constructor(config: AdditionalInfoStoreConfig) {
        this.onAllRead = config.onAllRead;
        this.onInfosRemoved = config.onInfosRemoved;
        this.setSortDirection(config.sortDirection ?? SortDirection.DESC);
        this.data.filter = config.filter ?? [USER, SYSTEM];
        this.data.unreads = config.firstUnreadInfo
            ? [config.firstUnreadInfo].filter((info) => removeInvalidInitialUnreads(info))
            : [];
        this.data.unreads.sort(compareInfos);
        makeObservable(this, {
            // observables
            data: observable,
            // computeds
            agencyEventNumberToIdMap: computed,
            agencyTypeGlobalAttrIds: computed,
            isAgencyTypeFilterActive: computed,
            earliestUnreadInfo: computed,
            activeRowAction: computed,
            editorState: computed,
            filter: computed,
            hasOlderPage: computed,
            hasNewerPage: computed,
            hasUnread: computed,
            hiddenPinnedCount: computed,
            initialSocketConnectionMade: computed,
            isFetchingNextPage: computed,
            isHighPriority: computed,
            isPinnedSectionExpanded: computed,
            isPageReloading: computed,
            latestKnownId: computed,
            newerCursor: computed,
            olderCursor: computed,
            pageFetchFailed: computed,
            pinnedEntries: computed,
            showPinnedDivider: computed,
            sortDirection: computed,
            unreadInfos: computed,
            visiblePinnedEntries: computed,
            // actions
            clearUnreadsBasedOnOldestUnread: action,
            markAsRead: action,
            markAsUnread: action,
            syncUnreadPriority: action,
            removeInfos: action,
            reset: action,
            setActiveRowAction: action,
            clearActiveRowAction: action,
            setFilter: action,
            syncAgencyTypeFilterForEvent: action,
            toggleAgencyTypeFilter: action,
            setLatestKnownId: action,
            setPinnedEntries: action,
            addPinnedEntry: action,
            removePinnedEntry: action,
            updatePinnedEntry: action,
            setInitialSocketConnectionMade: action,
            setIsFetchingNextPage: action,
            setIsHighPriority: action,
            setPagination: action,
            setPageFetchFailed: action,
            setSortDirection: action,
            storeEditorState: action,
            toggleIsPageReloading: action,
            togglePinnedSection: action,
            upsertAgencyEventNumberToIdMap: action,
            upsertInfos: action,
        });
    }

    get activeRowAction(): ActiveRowAction | null {
        return this.data.activeRowAction;
    }

    setActiveRowAction = (action: ActiveRowAction | null): void => {
        this.data.activeRowAction = action;
    };

    clearActiveRowAction = (): void => {
        this.data.activeRowAction = null;
    };

    get isHighPriority(): boolean {
        return this.data.isHighPriority;
    }

    get editorState(): EditorState | undefined {
        return this.data.editorState;
    }

    get initialSocketConnectionMade(): boolean {
        return this.data.initialSocketConnectionMade;
    }

    get isFetchingNextPage(): boolean {
        return this.data.isFetchingNextPage;
    }

    get olderCursor(): number | undefined {
        return this.data.pagination.olderCursor ?? undefined;
    }

    get newerCursor(): number | undefined {
        return this.data.pagination.newerCursor ?? undefined;
    }

    get hasOlderPage(): boolean {
        return this.data.pagination.hasOlderPage ?? false;
    }

    get hasNewerPage(): boolean {
        return this.data.pagination.hasNewerPage ?? false;
    }

    get latestKnownId(): number | undefined {
        return this.data.latestKnownId;
    }

    get pageFetchFailed(): boolean {
        return this.data.pageFetchFailed;
    }

    get filter(): AdditionalInfoType[] | undefined {
        return this.data.filter;
    }

    get isPageReloading(): boolean {
        return this.data.isPageReloading;
    }

    get sortDirection(): SortDirection | undefined {
        return this.data.sortDirection;
    }

    get earliestUnreadInfo(): { id: number } | null {
        return this.data.unreads.length > 0 ? this.data.unreads[0] : null;
    }

    get unreadInfos(): UnreadInfos {
        return {
            STANDARD_UPDATE:
                this.data.unreads.find((info) => info.priority === 'STANDARD_UPDATE') ?? null,
            HIGH_PRIORITY_UPDATE:
                this.data.unreads.find((info) => info.priority === 'HIGH_PRIORITY_UPDATE') ?? null,
        };
    }

    get hasUnread(): boolean {
        return this.data.unreads.length !== 0;
    }

    get agencyEventNumberToIdMap(): Record<string, number> {
        return this.data.agencyEventNumberToIdMap;
    }

    setIsHighPriority = (value: boolean): void => {
        this.data.isHighPriority = value;
    };

    storeEditorState = (value: EditorState): void => {
        this.data.editorState = value;
    };

    setInitialSocketConnectionMade = (value: boolean): void => {
        this.data.initialSocketConnectionMade = value;
    };

    setIsFetchingNextPage = (value: boolean): void => {
        this.data.isFetchingNextPage = value;
    };

    setPagination = (value: Partial<NarrativePagination>): void => {
        // Each BE response populates only the fields relevant to its shape:
        // HISTORIC continuation ships olderCursor/hasOlderPage; CATCHUP ships
        // newerCursor/hasNewerPage; bootstrap and aroundId additionally ship
        // bounds. The other-direction fields arrive as null. We treat null /
        // undefined as "absent" so a HISTORIC continuation doesn't wipe the
        // newer-side cursor/flag previously set by an aroundId response (and
        // vice versa) — losing that field would drop the FE out of bidirectional
        // mode on the next scroll.
        //
        // No caller passes null to mean "clear" — reset() mutates the
        // pagination object directly when it needs to wipe state.
        const filtered = Object.fromEntries(
            Object.entries(value).filter(([, v]) => v !== null && v !== undefined)
        ) as Partial<NarrativePagination>;
        this.data.pagination = { ...this.data.pagination, ...filtered };
    };

    setLatestKnownId = (value: number | undefined): void => {
        this.data.latestKnownId = value;
    };

    setPageFetchFailed = (value: boolean): void => {
        this.data.pageFetchFailed = value;
    };

    toggleIsPageReloading = (): void => {
        this.data.isPageReloading = !this.data.isPageReloading;
    };

    upsertAgencyEventNumberToIdMap = (agencyEventNumberToIdMap: Record<string, number>): void => {
        for (const agencyEventNumber in agencyEventNumberToIdMap) {
            this.data.agencyEventNumberToIdMap[agencyEventNumber] =
                agencyEventNumberToIdMap[agencyEventNumber];
        }
    };

    setSortDirection = (sort: SortDirection): void => {
        this.data.sortDirection = sort;
        this.listStore.setSortingDirective(sortDirectives[sort]);
    };

    setFilter = (filters: AdditionalInfoType[]): void => {
        this.data.filter = filters;
    };

    get agencyTypeGlobalAttrIds(): Set<number> {
        const included = new Set<number>();
        this.data.participatingAgencyTypeIds.forEach((id) => {
            if (!this.data.hiddenAgencyTypeIds.has(id)) {
                included.add(id);
            }
        });
        return included;
    }

    get isAgencyTypeFilterActive(): boolean {
        return this.data.participatingAgencyTypeIds.size > 0;
    }

    syncAgencyTypeFilterForEvent = (entityId: number, participatingIds: number[]): void => {
        const sameEvent = this.data.agencyTypeFilterEntityId === entityId;
        const next = new Set<number>(participatingIds);
        // Idempotent: a same-event resync with an unchanged class set writes no
        // observables, so the derived computed isn't invalidated and observers
        // don't re-render. The panel re-fires this effect whenever crossClassOptions
        // gets a fresh identity (metadata churn), which is often.
        if (sameEvent && setsEqual(next, this.data.participatingAgencyTypeIds)) {
            return;
        }
        this.data.participatingAgencyTypeIds = next;
        if (!sameEvent) {
            // Different event now owns this store — start from "show all".
            this.data.agencyTypeFilterEntityId = entityId;
            this.data.hiddenAgencyTypeIds = new Set<number>();
            return;
        }
        // Same event, class set may have changed (e.g. Request for Assistance).
        // New classes show by default (absent from hidden); prune hides for
        // classes that left so they can't linger.
        if (this.data.hiddenAgencyTypeIds.size > 0) {
            const pruned = new Set<number>();
            this.data.hiddenAgencyTypeIds.forEach((id) => {
                if (this.data.participatingAgencyTypeIds.has(id)) {
                    pruned.add(id);
                }
            });
            this.data.hiddenAgencyTypeIds = pruned;
        }
    };

    // Toggle checked state by flipping membership in the hidden set.
    toggleAgencyTypeFilter = (agencyTypeId: number): void => {
        const next = new Set(this.data.hiddenAgencyTypeIds);
        if (next.has(agencyTypeId)) {
            next.delete(agencyTypeId);
        } else {
            next.add(agencyTypeId);
        }
        this.data.hiddenAgencyTypeIds = next;
    };

    reset = (config: AdditionalInfoStoreConfig = {}): void => {
        if (config.sortDirection) {
            this.setSortDirection(config.sortDirection);
        } else if (this.data.sortDirection !== undefined) {
            this.setSortDirection(this.data.sortDirection);
        }
        this.data.filter = config.filter ?? this.data.filter;
        this.data.unreads = config.firstUnreadInfo
            ? [config.firstUnreadInfo].filter((info) => removeInvalidInitialUnreads(info))
            : this.data.unreads;
        this.data.unreads.sort(compareInfos);
        this.data.activeRowAction = null;
        this.listStore.setItems([]);
        // Clear aroundId-mode bookkeeping so the next bootstrap re-seeds cleanly.
        // AdditionalInfoRenderer's `isAroundIdMode` check keys off `olderCursor`;
        // stale values would misroute the edge triggers.
        this.data.pagination = {
            ...this.data.pagination,
            olderCursor: undefined,
            newerCursor: undefined,
            hasOlderPage: undefined,
            hasNewerPage: undefined,
            oldestEntryId: undefined,
            newestEntryId: undefined,
        };
        this.data.latestKnownId = undefined;
    };

    upsertInfos = (rows: AdditionalInfoView[], markUnread = false): void => {
        if (markUnread) {
            const nonSystemInfoRows = rows.filter((row) => row.type !== AdditionalInfoType.SYSTEM);
            this.markAsUnread(nonSystemInfoRows as UnreadInfo[]);
        }
        this.listStore.upsertItems(rows);
        for (const row of rows) {
            // Keep the unread indicator's priority in step with a priority edit.
            // Both the hook edit path and realtime edits land here (all realtime
            // updates go through upsertInfos), so wiring the sync at this single
            // seam covers both. The sink resolves to the active notification source
            // (see `onSyncUnreadPriority`); it no-ops unless the entry is currently
            // unread and its priority changed — an already-read entry is never re-added.
            // Guard first: only the two update priorities are valid unread buckets, so
            // a null/unknown priority must never reach the sink (it would overwrite a
            // matching unread entry into a bucket the indicator can't see).
            if (isUnreadPriority(row.priority)) {
                this.onSyncUnreadPriority?.(row.id, row.priority);
            }
            // Advance latestKnownId past anything newer we've now observed (WS create,
            // CATCHUP scroll). Bootstrap is a no-op here because `fetchNextPage`
            // (in use-additional-infos.tsx) seeds latestKnownId from
            // pagination.newestEntryId first, and bootstrap rows can't have
            // ids > newestEntryId.
            if (this.data.latestKnownId === undefined || row.id > this.data.latestKnownId) {
                this.data.latestKnownId = row.id;
            }
        }
    };

    removeInfos = (ids: number[]): void => {
        this.listStore.removeItems(ids);
        this.markAsRead(ids.map((id) => ({ id })));
        this.onInfosRemoved?.(ids);
        // A removed narrative (UI delete or WS delete/evict) must also leave the
        // pinned section — pinnedEntries is a separate array the list splice never
        // touches. No-op for stores that never populate it (e.g. ProQA).
        const removedIds = new Set(ids);
        if (this.data.pinnedEntries.some((entry) => removedIds.has(entry.id))) {
            this.data.pinnedEntries = this.data.pinnedEntries.filter(
                (entry) => !removedIds.has(entry.id)
            );
        }
        // Walk latestKnownId back if the row at the tip was the one deleted, else
        // the "Jump to Latest" target would point at a now-missing row.
        if (this.data.latestKnownId !== undefined && ids.includes(this.data.latestKnownId)) {
            const remaining = this.listStore.data.list.map((item) => item.id);
            this.data.latestKnownId = remaining.length
                ? Math.max(...remaining)
                : this.data.pagination.oldestEntryId;
        }
    };

    markAsUnread = (unreadInfos: UnreadInfo[]): void => {
        this.data.unreads.push(...unreadInfos);
        this.data.unreads.sort(compareInfos);
    };

    // This function is used when the user scrolls to the oldest unread additional info, and the
    // requirement is that all infos become unread.
    clearUnreadsBasedOnOldestUnread = (readInfos: { id: number }[]): void => {
        if (!Array.isArray(readInfos) || readInfos.length === 0 || this.data.unreads.length === 0)
            return;
        const readIds = readInfos.map((info) => info.id);
        if (readIds.includes(this.data.unreads[0].id)) {
            this.data.unreads = [];
            this.onAllRead?.();
        }
    };

    // This function is used when an additional info is deleted due to a websocket message, and the
    // specific info should become read, but the other infos must remain unread.
    markAsRead = (readInfos: { id: number }[]): void => {
        if (!Array.isArray(readInfos) || readInfos.length === 0 || this.data.unreads.length === 0)
            return;
        const readIds = readInfos.map((info) => info.id);
        this.data.unreads = this.data.unreads.filter(({ id }) => !readIds.includes(id));
        if (this.data.unreads.length === 0) {
            this.onAllRead?.();
        }
    };

    // Notification state machine for priority edits. `unreadInfos` buckets unreads by
    // their stored priority, so re-bucketing the unread indicator is simply updating the
    // stored priority of the matching *unread* entry. The transition matrix this satisfies:
    //
    //   Transition             | Entry state  | Outcome
    //   -----------------------|--------------|------------------------------------------
    //   STANDARD -> HIGH       | unread       | match found -> set HIGH  -> indicator upgrades
    //   STANDARD -> HIGH       | already read | no match    -> no-op (never re-notify)
    //   HIGH -> STANDARD       | unread       | match found -> set STD   -> indicator downgrades
    //   HIGH -> STANDARD       | already read | no match    -> no-op (never re-added)
    //   text-only (prio same)  | any          | priority equal -> inner guard skips -> no-op
    //
    // Single pass, no re-sort: `data.unreads` is ordered by `compareInfos`, which keys
    // on `id` only — priority is not a sort key, so changing it never reorders.
    syncUnreadPriority = (id: number, newPriority: UnreadInfo['priority']): void => {
        for (const unread of this.data.unreads) {
            if (unread.id === id) {
                // Unread + priority changed. Already-read entries never reach here (no
                // match). A text-only edit (priority unchanged) is skipped by the guard below.
                if (unread.priority !== newPriority) {
                    unread.priority = newPriority;
                }
                return;
            }
        }
    };

    private readonly defaultVisibleCount = 2;

    get pinnedEntries(): AdditionalInfoView[] {
        return this.data.pinnedEntries;
    }

    get isPinnedSectionExpanded(): boolean {
        return this.data.isPinnedSectionExpanded;
    }

    get visiblePinnedEntries(): AdditionalInfoView[] {
        // pinnedEntries are stored newest-pin-first (DESC by pinnedDateUtc). The
        // collapsed view always surfaces the most-recently-pinned entries
        // (recency = relevance), so we select the visible slice off the DESC
        // order regardless of direction.
        const visible =
            this.isPinnedSectionExpanded || this.pinnedEntries.length <= this.defaultVisibleCount
                ? this.pinnedEntries
                : this.pinnedEntries.slice(0, this.defaultVisibleCount);
        // The pinned section follows the panel's display direction:
        // DESC shows newest pin at top, ASC shows oldest pin at top. Reverse a
        // copy so the canonical store order stays untouched.
        return this.data.sortDirection === SortDirection.ASC ? [...visible].reverse() : visible;
    }

    get showPinnedDivider(): boolean {
        return this.pinnedEntries.length > this.defaultVisibleCount;
    }

    get hiddenPinnedCount(): number {
        return Math.max(0, this.pinnedEntries.length - this.defaultVisibleCount);
    }

    setPinnedEntries = (entries: AdditionalInfoView[]): void => {
        this.data.pinnedEntries = entries;
    };

    // Insert a newly-pinned entry into the canonical DESC-by-pinnedDateUtc order
    // (newest pin first). Re-pinning an already-present entry (pin → unpin → pin)
    // gives it a fresh pinnedDateUtc, so we drop any existing copy first and
    // re-insert at its new position. visiblePinnedEntries
    // handles the ASC reversal + collapse off this canonical order untouched.
    addPinnedEntry = (entry: AdditionalInfoView): void => {
        const withoutExisting = this.data.pinnedEntries.filter((e) => e.id !== entry.id);
        // Descending by pinnedDateUtc: find the first existing entry pinned
        // strictly earlier than the new one and splice in before it. Entries
        // missing pinnedDateUtc sort to the end (treated as oldest).
        const newPinnedAt = entry.pinnedDateUtc ?? '';
        const insertAt = withoutExisting.findIndex((e) => (e.pinnedDateUtc ?? '') < newPinnedAt);
        if (insertAt === -1) {
            withoutExisting.push(entry);
        } else {
            withoutExisting.splice(insertAt, 0, entry);
        }
        this.data.pinnedEntries = withoutExisting;
    };

    removePinnedEntry = (id: number): void => {
        this.data.pinnedEntries = this.data.pinnedEntries.filter((e) => e.id !== id);
    };

    updatePinnedEntry = (patch: Partial<AdditionalInfoView> & { id: number }): void => {
        this.data.pinnedEntries = this.data.pinnedEntries.map((entry) =>
            entry.id === patch.id ? { ...entry, ...patch } : entry
        );
    };

    togglePinnedSection = (): void => {
        this.data.isPinnedSectionExpanded = !this.data.isPinnedSectionExpanded;
    };
}
